import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const money = (cents, currency = "nzd") => new Intl.NumberFormat("en-NZ", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
const date = value => value ? new Date(value).toLocaleDateString("en-NZ") : "Unavailable";

export default function BillingPlanChange({ target, operationId, onClose, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const open = !!(target || operationId);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setPreview(null); setResult(null); setError(""); setBusy(true);
    const request = operationId ? api.get(`/subscription/change/${operationId}`) : api.post("/subscription/change-preview", { target_plan: target });
    request.then(({ data }) => {
      if (!active) return;
      if (operationId) { setPreview(data.review); setResult(data); }
      else setPreview(data);
    }).catch(e => { if (active) setError(e.response?.data?.detail || "Could not load the Stripe preview. No change was made."); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [target, operationId, open]);

  const confirm = async () => {
    if (!preview || pending.current) return;
    pending.current = true; setBusy(true); setError("");
    try {
      const { data } = await api.post("/subscription/change", { target_plan: preview.target_plan, preview_id: preview.preview_id });
      setResult(data); onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || "Confirmation was not received. Retry this same request safely.");
    } finally { pending.current = false; setBusy(false); }
  };
  const refresh = async () => {
    if (pending.current || !result?.operation_id) return;
    pending.current = true; setBusy(true); setError("");
    try { const { data } = await api.get(`/subscription/change/${result.operation_id}`); setResult(data); onChanged(); }
    catch (e) { setError(e.response?.data?.detail || "Could not refresh payment status."); }
    finally { pending.current = false; setBusy(false); }
  };
  const done = result?.terminal || ["complete", "scheduled"].includes(result?.state);
  return <Dialog open={open} onOpenChange={value => !value && !busy && onClose()}>
    <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{preview?.direction === "downgrade" ? "Review downgrade" : "Review upgrade"}</DialogTitle>
        <DialogDescription>Review your plan and Stripe billing details before confirming.</DialogDescription>
      </DialogHeader>
      {busy && !preview && <p role="status">Loading billing details…</p>}
      {error && <p role="alert" className="text-sm text-red-600">{typeof error === "string" ? error : error.message || "This plan change needs a billing review."}</p>}
      {preview && <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <dt>Current plan</dt><dd className="capitalize font-semibold">{preview.current_plan?.replaceAll("_", " ")} · {money(preview.current_plan_price * 100)} / month<br />Up to {preview.current_limit} users</dd>
        <dt>New plan</dt><dd className="capitalize font-semibold">{preview.target_plan?.replaceAll("_", " ")} · {money(preview.target_plan_price * 100)} / month<br />Up to {preview.target_limit} users</dd>
        {preview.direction === "upgrade" && <><dt>Due now</dt><dd className="font-bold">{money(preview.amount_due, preview.currency)}</dd></>}
        <dt>Next recurring plan price</dt><dd>{money(preview.next_recurring_amount)} / month</dd>
        <dt>{preview.direction === "downgrade" ? "Change takes effect" : "Renewal date"}</dt><dd>{date(preview.current_period_end)}</dd>
      </dl>}
      {preview && <p className="text-xs text-zinc-500">{preview.direction === "upgrade" ? "Due now is calculated by Stripe for this change, including applicable credits, tax and discounts. The same proration time is used when confirming." : "Your current plan remains active until renewal. New active users must also fit the scheduled lower limit; cancel the downgrade to add more."} Recurring plan prices are before applicable Stripe tax or discounts.</p>}
      {result && <div role="status" className="rounded border p-3 text-sm">
        {result.state === "complete" ? (preview?.direction === "downgrade" ? "Plan change complete. Your new plan is active." : "Upgrade complete. Your new plan is active.") : result.state === "scheduled" ? `Downgrade scheduled for ${date(result.effective_at)}. Your current entitlement remains active until then.` : result.state === "payment_action_required" ? "Stripe needs payment confirmation. Your current plan remains unchanged until payment is confirmed." : result.state === "payment_failed" ? "Payment needs attention. Your higher user limit has not been activated." : "The change is awaiting confirmation. Your higher user limit has not been activated."}
        {result.payment_url && <a className="block mt-2 underline" href={result.payment_url} target="_blank" rel="noopener noreferrer">Complete payment securely with Stripe</a>}
      </div>}
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>{result ? "Close" : "Cancel"}</Button>
        {done && result.state === "complete" && <Button asChild><Link to="/app/team">Return to Team</Link></Button>}
        {!done && result && <Button variant="outline" disabled={busy} onClick={refresh}>Refresh status</Button>}
        {!done && preview && !result?.payment_url && <Button disabled={busy} onClick={confirm}>{busy ? "Confirming…" : result ? "Retry confirmation" : preview.direction === "downgrade" ? "Schedule downgrade" : "Confirm upgrade"}</Button>}
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
